import { callGateway, type CallerIdentity } from "./gateway";
import { ApiError } from "./errors";
import { config } from "../config";
import type { RosterRow, RowProblem } from "../domain/rosterCsv";

/**
 * The sponsor roster, published by radaccess v1.51 and served by radcustomer.
 *
 *   POST /operator/{ns}/roster/batch[?dry_run=true]
 *   GET  /operator/{ns}/roster/members
 *   PUT  /operator/{ns}/roster/member/{id}/revoke
 *   PUT  /operator/{ns}/roster/member/{id}/reinstate
 *   GET  /operator/{ns}/roster/sponsor/settings
 *   PUT  /operator/{ns}/roster/sponsor/settings
 *
 * Revoke ends the member's sponsored service: their own plan comes back, or their
 * next sign-in is refused. Reinstate lifts that. An upload also moves members
 * who already have a subscriber account onto their plan (design §12).
 *
 * 🔑 A sponsor's organisation is bound to its login server-side, so no call
 * here names one. radcustomer refuses a sponsor that names another. An
 * operator login must name one, and no route lists organisations yet -- which
 * is why the screen is a sponsor's screen for now.
 *
 * Design: radius-wiki wiki/sponsor-roster-console-design.md §4 and §8.
 */

const PREFIX = "/operator";
const ns = (n: string) => encodeURIComponent(n);

export type MemberStatus = "pending" | "activated" | "revoked";

export interface RosterUpload {
  filename: string;
  file_sha256: string;
  rows: RosterRow[];
}

/** radcustomer model.RosterOrgInfo. */
export interface RosterOrganisation {
  name: string;
  status: string;
  member_ref_label: string;
  group_label_label: string;
  location_label_label: string;
}

/** radcustomer model.RosterMember. */
export interface RosterMember {
  id: number;
  mobile: string;
  full_name: string;
  category: string;
  member_ref: string;
  group_label?: string;
  location_label?: string;
  /** plan_id, custom, default -- or category for a row from before plans were chosen per row. */
  plan_source: "plan_id" | "custom" | "default" | "category";
  groupname?: string;
  speed_mbps?: number;
  valid_days?: number;
  valid_until?: string;
  status: MemberStatus;
  uploaded_at: string;
  activated_at?: string;
  revoked_at?: string;
  /** The plan last given to the member's subscriber account, and the last failure to give it. */
  applied_groupname?: string;
  apply_error?: string;
  /** Revoke refused the member's next sign-in (they had no plan of their own). */
  blocked?: boolean;
  reinstated_at?: string;
}

/** What an upload did to members who already have a subscriber account. */
export interface AccountsResult {
  applied: number;
  failed: { line?: number; mobile: string; message: string }[] | null;
  /** Left for the next upload: the upload ran out of time. */
  waiting: number;
  unavailable?: string;
}

/** restored: back on their own plan · blocked: next sign-in refused · none: had not signed up · unchanged: someone else manages the account now. */
export type RevokeAccess = "restored" | "blocked" | "none" | "unchanged";

export interface MemberPage {
  organisation: RosterOrganisation;
  members: RosterMember[] | null;
  /** 0 when there is no further page. */
  next_cursor: number;
}

export const checkBatch = (i: CallerIdentity, n: string, u: RosterUpload) =>
  callGateway<{ dry_run: boolean; rows: number; plans_to_create?: string[] }>(i, config().gatewayBase, `${PREFIX}/${ns(n)}/roster/batch?dry_run=true`, {
    method: "POST",
    body: u,
  });

export const uploadBatch = (i: CallerIdentity, n: string, u: RosterUpload) =>
  callGateway<{ batch_id: string; rows: number; plans_created?: string[]; accounts?: AccountsResult }>(i, config().gatewayBase, `${PREFIX}/${ns(n)}/roster/batch`, {
    method: "POST",
    body: u,
  });

export interface MemberQuery {
  /** Required from an operator login, which may see several organisations; a sponsor's is bound to its login. */
  organisation_id?: string | undefined;
  status?: MemberStatus | undefined;
  q?: string | undefined;
  cursor?: number | undefined;
}

export const listMembers = (i: CallerIdentity, n: string, query: MemberQuery) => {
  const p = new URLSearchParams();
  if (query.organisation_id) p.set("organisation_id", query.organisation_id);
  if (query.status) p.set("status", query.status);
  if (query.q && query.q.trim()) p.set("q", query.q.trim());
  if (query.cursor) p.set("cursor", String(query.cursor));
  const qs = p.toString();
  return callGateway<MemberPage>(i, config().gatewayBase, `${PREFIX}/${ns(n)}/roster/members${qs ? `?${qs}` : ""}`);
};

const memberAction = async <T>(i: CallerIdentity, n: string, id: number, action: "revoke" | "reinstate") => {
  if (!Number.isInteger(id) || id < 1) {
    throw new ApiError("fail-closed", `Not a member id: ${id}. The request was not sent.`);
  }
  return callGateway<T>(i, config().gatewayBase, `${PREFIX}/${ns(n)}/roster/member/${id}/${action}`, {
    method: "PUT",
  });
};

export const revokeMember = (i: CallerIdentity, n: string, id: number) =>
  memberAction<{ id: number; status: "revoked"; access?: RevokeAccess }>(i, n, id, "revoke");

export const reinstateMember = (i: CallerIdentity, n: string, id: number) =>
  memberAction<{ id: number; status: "activated" | "pending" }>(i, n, id, "reinstate");

/** The per-line problems radcustomer returned with a refusal, if any. */
export function rowErrorsOf(err: unknown): RowProblem[] {
  if (!(err instanceof ApiError)) return [];
  const rows = (err.body as { rows?: unknown } | null)?.rows;
  if (!Array.isArray(rows)) return [];
  return rows.filter(
    (r): r is RowProblem =>
      !!r && typeof r === "object" && typeof (r as RowProblem).line === "number" && typeof (r as RowProblem).message === "string",
  );
}

/**
 * A sponsor's own contact settings (design §13.8): the address the weekly
 * summary goes to, and whether it wants one.
 *
 * 🔑 NOTHING HERE NAMES A SPONSOR. radcustomer takes the login from the token,
 * so this screen can only ever edit the signed-in sponsor's own row -- and the
 * gateway refuses the route to an operator outright, because pointing somebody
 * else's mail elsewhere is not an operator's to do.
 */
export interface SponsorSettings {
  /** "" means no address, which is also how a sponsor stops the summary. */
  email: string;
  /** The weekly summary switch. */
  digest: boolean;
}

export const getSponsorSettings = (i: CallerIdentity, n: string) =>
  callGateway<SponsorSettings>(i, config().gatewayBase, `${PREFIX}/${ns(n)}/roster/sponsor/settings`);

export const saveSponsorSettings = (i: CallerIdentity, n: string, s: SponsorSettings) =>
  callGateway<SponsorSettings>(i, config().gatewayBase, `${PREFIX}/${ns(n)}/roster/sponsor/settings`, {
    method: "PUT",
    // 🪤 radcustomer decodes with DisallowUnknownFields: an extra key here is a
    // 400, not a field it ignores. Send exactly the two it declares.
    body: { email: s.email.trim(), digest: s.digest },
  });
